import { largestRemainder } from "@/lib/tool-kit/study/allocate";
import {
  type DayNumber,
  daysMatching,
  parseIsoDay,
} from "@/lib/tool-kit/study/dates";

/**
 * The assignment planner's model: step templates and the date arithmetic that spreads
 * them over the days a student actually has.
 *
 * A template is a list of steps with a share of the total time each. The shares are a
 * judgement, not a law — research-heavy work front-loads searching and reading, a lab
 * report puts more into analysis and drafting, a presentation trades proofreading for
 * rehearsal — and the page says so. The step wording lives in the widget's strings; this
 * module only knows each step's key, share and links.
 */

export type AssignmentType =
  | "research-paper"
  | "essay"
  | "lab-report"
  | "presentation"
  | "literature-review";

export const ASSIGNMENT_TYPES: readonly AssignmentType[] = [
  "research-paper",
  "essay",
  "lab-report",
  "presentation",
  "literature-review",
];

/** Where a step's "next tool" link points. Un-prefixed; `LocaleLink` adds the locale. */
export const ASSIGNMENT_LINKS = {
  citationGenerator: "/",
  citationChecker: "/tools/citation-checker",
  grammarChecker: "/tools/grammar-checker",
  essayOutline: "/tools/essay-outline-generator",
  thesisStatement: "/tools/thesis-statement-generator",
  researchQuestion: "/tools/research-question-generator",
  hypothesis: "/tools/hypothesis-generator",
  annotatedBibliography: "/tools/annotated-bibliography-maker",
  wordCounter: "/tools/word-counter",
  statistics: "/tools/statistics-calculator",
  readingTime: "/tools/reading-time-calculator",
  teleprompter: "/tools/online-teleprompter",
  researchPaperGuide: "/guides/how-to-write-research-paper",
  thesisGuide: "/guides/how-to-write-thesis-statement",
  findSourcesGuide: "/guides/how-to-find-scholarly-sources",
  evaluateSourcesGuide: "/guides/how-to-evaluate-sources",
  notesGuide: "/guides/how-to-take-research-notes",
  readingPapersGuide: "/guides/how-to-read-academic-papers",
  paraphrasingGuide: "/guides/paraphrasing-vs-quoting",
  revisionGuide: "/guides/revision-strategies",
  methodologyGuide: "/guides/how-to-write-methodology",
  abstractGuide: "/guides/how-to-write-abstract",
  presentDataGuide: "/guides/how-to-present-data",
  presentationCitationGuide: "/guides/cite-sources-in-presentation",
  databasesGuide: "/guides/library-databases",
  organizeGuide: "/guides/how-to-organize-research",
  literatureReviewGuide: "/guides/how-to-write-literature-review",
} as const;

export type AssignmentLink = keyof typeof ASSIGNMENT_LINKS;

export interface StepTemplate {
  /** Key into the widget's `stepNames` and `stepTips`. */
  key: string;
  /** Percent of the available days. A template's shares sum to 100. */
  share: number;
  links: readonly AssignmentLink[];
}

export const ASSIGNMENT_TEMPLATES: Record<
  AssignmentType,
  readonly StepTemplate[]
> = {
  "research-paper": [
    { key: "understand", share: 5, links: ["researchPaperGuide"] },
    {
      key: "research",
      share: 20,
      links: ["researchQuestion", "findSourcesGuide"],
    },
    { key: "notes", share: 15, links: ["notesGuide"] },
    { key: "outline", share: 10, links: ["thesisStatement", "essayOutline"] },
    { key: "draft", share: 25, links: ["wordCounter"] },
    { key: "revise", share: 12, links: ["revisionGuide"] },
    { key: "cite", share: 6, links: ["citationGenerator"] },
    { key: "proofread", share: 5, links: ["grammarChecker"] },
    { key: "submit", share: 2, links: ["citationChecker"] },
  ],
  essay: [
    { key: "essayUnderstand", share: 5, links: ["thesisGuide"] },
    { key: "essayResearch", share: 15, links: ["evaluateSourcesGuide"] },
    { key: "notes", share: 10, links: ["paraphrasingGuide"] },
    {
      key: "essayOutline",
      share: 12,
      links: ["thesisStatement", "essayOutline"],
    },
    { key: "draft", share: 28, links: ["wordCounter"] },
    { key: "revise", share: 15, links: ["revisionGuide"] },
    { key: "cite", share: 5, links: ["citationGenerator"] },
    { key: "proofread", share: 8, links: ["grammarChecker"] },
    { key: "submit", share: 2, links: ["citationChecker"] },
  ],
  "lab-report": [
    { key: "labUnderstand", share: 5, links: ["hypothesis"] },
    { key: "labData", share: 20, links: ["statistics"] },
    { key: "labBackground", share: 10, links: ["readingPapersGuide"] },
    { key: "labOutline", share: 8, links: ["methodologyGuide"] },
    { key: "labDraft", share: 30, links: ["abstractGuide"] },
    { key: "revise", share: 12, links: ["revisionGuide"] },
    { key: "cite", share: 5, links: ["citationGenerator"] },
    { key: "proofread", share: 8, links: ["grammarChecker"] },
    { key: "submit", share: 2, links: ["citationChecker"] },
  ],
  presentation: [
    { key: "presUnderstand", share: 5, links: ["readingTime"] },
    { key: "research", share: 15, links: ["findSourcesGuide"] },
    { key: "notes", share: 10, links: ["notesGuide"] },
    { key: "presOutline", share: 12, links: ["essayOutline"] },
    { key: "presSlides", share: 23, links: ["presentDataGuide"] },
    { key: "revise", share: 10, links: ["revisionGuide"] },
    { key: "presCite", share: 5, links: ["presentationCitationGuide"] },
    { key: "presRehearse", share: 18, links: ["teleprompter"] },
    { key: "presDeliver", share: 2, links: [] },
  ],
  "literature-review": [
    { key: "understand", share: 5, links: ["literatureReviewGuide"] },
    { key: "litSearch", share: 20, links: ["databasesGuide"] },
    {
      key: "litRead",
      share: 25,
      links: ["readingPapersGuide", "annotatedBibliography"],
    },
    { key: "litOutline", share: 10, links: ["organizeGuide"] },
    { key: "litDraft", share: 20, links: ["paraphrasingGuide"] },
    { key: "revise", share: 10, links: ["revisionGuide"] },
    { key: "cite", share: 4, links: ["citationGenerator"] },
    { key: "proofread", share: 4, links: ["grammarChecker"] },
    { key: "submit", share: 2, links: ["citationChecker"] },
  ],
};

/**
 * The days a plan can use: every chosen weekday from the start date up to the day
 * before the due date, then the due date itself.
 *
 * The due date is always in, whatever its weekday — work is handed in on the day it is
 * due even if that is a Sunday — and it is always last, which is what pins the final
 * step to it.
 */
export function assignmentWorkDays(
  start: DayNumber,
  due: DayNumber,
  weekdays: ReadonlySet<number>
): DayNumber[] {
  const days = due > start ? daysMatching(start, due - 1, weekdays) : [];
  days.push(due);
  return days;
}

export interface PlannedStep extends StepTemplate {
  /** Whole working days given to this step; 0 when it shares a day with the one before. */
  days: number;
  /** The first day to work on it. */
  first: DayNumber;
  /** Finish by the end of this day. */
  due: DayNumber;
}

/**
 * Lay the steps end to end over `workDays`.
 *
 * Days are shared out by largest remainder, so they always add up to the days available.
 * A step that rounds to zero days — nine steps in a three-day window — is done on the
 * same day the previous step finishes, which is honest about a tight deadline rather
 * than pretending there is a day for it.
 */
export function planAssignment(
  steps: readonly StepTemplate[],
  workDays: readonly DayNumber[]
): PlannedStep[] {
  if (workDays.length === 0) {
    return [];
  }
  const allocation = largestRemainder(
    steps.map((step) => step.share),
    workDays.length
  );
  let used = 0;
  return steps.map((step, index) => {
    const days = allocation[index];
    const before = used;
    used += days;
    const due = workDays[Math.max(used - 1, 0)];
    const first = days > 0 ? workDays[before] : due;
    return { ...step, days, first, due };
  });
}

export interface AssignmentDates {
  start: DayNumber | null;
  due: DayNumber | null;
  /** Set when both dates parse but cannot make a plan. */
  problem: "dueBeforeStart" | "tooLong" | null;
}

/** Read the start and due fields; incomplete dates are no plan yet, not an error. */
export function checkAssignmentDates(
  start: string,
  due: string,
  maxSpan: number
): AssignmentDates {
  const startDay = parseIsoDay(start);
  const dueDay = parseIsoDay(due);
  if (startDay === null || dueDay === null) {
    return { start: null, due: null, problem: null };
  }
  if (dueDay < startDay) {
    return { start: null, due: null, problem: "dueBeforeStart" };
  }
  if (dueDay - startDay > maxSpan) {
    return { start: null, due: null, problem: "tooLong" };
  }
  return { start: startDay, due: dueDay, problem: null };
}
